import { ProviderPracticeDetailsFields } from "./provider-practice-details-fields";
import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import {
  ProviderNpiSelect,
  type ProviderNpiValue,
} from "@/components/Selects/ProviderNpiSelect";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const NPI_DIGIT_COUNT = 10;
const PROFESSIONAL_TYPES_STALE_MS = 5 * 60 * 1000;

export type ProfessionalDetailsValue = {
  npiNumber: string;
  providerType: string;
  providerRole: string;
  specialtyCodes: string[];
  practiceSetting: string;
  primaryPracticeState: string;
  additionalPracticeStates: string[];
  languages: string[];
};

export type ProfessionalNpiSelection = {
  npiNumber: string;
  taxonomyCodes: string[];
  providerType: string;
  providerRole: string;
  name: { first: string; middle?: string; last: string };
};

export function ProviderProfessionalDetailsForm({
  value,
  disabled,
  allowNoNpi,
  onChange,
  onNpiSelect,
}: {
  value: ProfessionalDetailsValue;
  disabled: boolean;
  allowNoNpi: boolean;
  onChange: (patch: Partial<ProfessionalDetailsValue>) => void;
  onNpiSelect: (selection: ProfessionalNpiSelection) => void;
}) {
  const trpc = useTRPC();
  const id = useId();
  const [entryMethod, setEntryMethod] = useState(
    value.npiNumber || value.providerType ? "manual" : "search",
  );
  const [hasNoNpi, setHasNoNpi] = useState(false);
  const [typeNotMatched, setTypeNotMatched] = useState(false);
  const types = useQuery(
    trpc.healthcareProfessionalTypes.getAll.queryOptions(undefined, {
      staleTime: PROFESSIONAL_TYPES_STALE_MS,
    }),
  );
  const professionalType = types.data?.find((type) =>
    [type.code, type.id, type.name, ...type.aliases].some(
      (candidate) =>
        candidate?.toLowerCase() === value.providerType.toLowerCase(),
    ),
  );
  let selectedType;
  if (professionalType) {
    selectedType = professionalType;
  } else if (value.providerType || value.providerRole) {
    // Imported values remain visible until the provider deliberately replaces
    // them. Rendering the editor must not normalize or erase an existing role.
    selectedType = {
      id: value.providerType,
      name: value.providerRole || value.providerType,
    };
  }

  function selectNpi(selection: ProviderNpiValue | null) {
    if (!selection) {
      return;
    }
    const matchedType = types.data?.find(
      (type) => type.id === selection.providerTypeId,
    );
    setTypeNotMatched(!matchedType?.code);
    setHasNoNpi(false);
    onNpiSelect({
      npiNumber: String(selection.npi),
      taxonomyCodes: [
        ...new Set(
          selection.specialties
            .map((specialty) => specialty.code)
            .filter(Boolean),
        ),
      ],
      providerType: matchedType?.code ?? "",
      providerRole: matchedType?.name ?? "",
      name: {
        first: selection.firstName,
        middle: selection.middleName ?? undefined,
        last: selection.lastName,
      },
    });
    setEntryMethod("manual");
  }

  return (
    <fieldset className={PROFILE_FORM_STYLES.section}>
      <legend className="sr-only">Professional details</legend>
      <p aria-hidden="true" className={PROFILE_FORM_STYLES.sectionHeading}>
        Professional details
      </p>
      <Tabs value={entryMethod} onValueChange={setEntryMethod}>
        <TabsList className="grid h-auto w-full grid-cols-2 rounded-xl border border-slate-200 bg-slate-50 p-1">
          <TabsTrigger
            value="search"
            disabled={disabled}
            className="whitespace-normal rounded-lg py-2.5 text-xs"
          >
            Search NPI database
          </TabsTrigger>
          <TabsTrigger
            value="manual"
            disabled={disabled}
            className="whitespace-normal rounded-lg py-2.5 text-xs"
          >
            Enter details manually
          </TabsTrigger>
        </TabsList>
        <TabsContent value="search" className="mt-4 space-y-3">
          <Label
            htmlFor={`${id}-npi-search`}
            className={PROFILE_FORM_STYLES.label}
          >
            Search by name or NPI number
          </Label>
          <ProviderNpiSelect
            inputId={`${id}-npi-search`}
            appearance="profile"
            disabled={
              disabled || types.isPending || (types.isError && !types.data)
            }
            onChange={selectNpi}
            placeholder="Enter your full name or NPI number"
          />
          <p className="text-xs leading-relaxed text-slate-500">
            Choose your record to fill your name, provider type, NPI and
            specialties. Review the details before saving.
          </p>
          {types.isError && (
            <p role="alert" className="text-xs text-red-700">
              Could not load provider types.{" "}
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-xs"
                disabled={disabled || types.isFetching}
                onClick={() => void types.refetch()}
              >
                Try again
              </Button>
            </p>
          )}
          {allowNoNpi && (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-xs text-primary"
              disabled={disabled}
              onClick={() => {
                setHasNoNpi(true);
                onChange({ npiNumber: "" });
                setEntryMethod("manual");
              }}
            >
              I don’t have an NPI
            </Button>
          )}
        </TabsContent>
        <TabsContent value="manual" className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label
              htmlFor={`${id}-provider-type`}
              className={PROFILE_FORM_STYLES.label}
            >
              Provider type / professional role
            </Label>
            <HealthcareProfessionalTypeSelect
              inputId={`${id}-provider-type`}
              appearance="profile"
              value={selectedType}
              disabled={disabled}
              onChange={(selection) => {
                if (Array.isArray(selection)) {
                  return;
                }
                setTypeNotMatched(false);
                onChange({
                  providerType: selection?.code ?? "",
                  providerRole: selection?.name ?? "",
                });
              }}
            />
            {typeNotMatched && (
              <p role="status" className="text-xs text-amber-700">
                Select your provider type; the NPI record did not identify a
                matching type.
              </p>
            )}
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label
              htmlFor={`${id}-npi-number`}
              className={PROFILE_FORM_STYLES.label}
            >
              NPI number
            </Label>
            <Input
              id={`${id}-npi-number`}
              value={value.npiNumber}
              inputMode="numeric"
              maxLength={NPI_DIGIT_COUNT}
              className={PROFILE_FORM_STYLES.control}
              placeholder="10-digit NPI"
              disabled={disabled || (allowNoNpi && hasNoNpi)}
              onChange={(event) =>
                onChange({ npiNumber: event.target.value.replace(/\D/g, "") })
              }
            />
            {allowNoNpi && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-no-npi`}
                  checked={hasNoNpi}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    setHasNoNpi(checked === true);
                    if (checked === true) {
                      onChange({ npiNumber: "" });
                    }
                  }}
                />
                <Label
                  htmlFor={`${id}-no-npi`}
                  className="text-xs font-normal text-slate-600"
                >
                  I don’t have an NPI
                </Label>
              </div>
            )}
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label
              htmlFor={`${id}-specialties`}
              className={PROFILE_FORM_STYLES.label}
            >
              Specialties
            </Label>
            <SpecialtiesSelect
              inputId={`${id}-specialties`}
              appearance="profile"
              isMulti
              value={value.specialtyCodes}
              disabled={disabled}
              onChange={(codes) =>
                onChange({ specialtyCodes: Array.isArray(codes) ? codes : [] })
              }
            />
          </div>
          <ProviderPracticeDetailsFields
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
        </TabsContent>
      </Tabs>
    </fieldset>
  );
}
